import { arrayToggle } from 'common/ArrayUtilities';
import { QueryParams } from 'common/urls/QueryParams';
import React, { useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { useDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import {
  AllergenGluten,
  DietaryDairyFree,
  DietaryHalal,
  DietaryKosher,
  DietaryLowFODMAP,
  DietaryOrganic,
  DietaryPaleo,
  DietaryVegan,
  DietaryVegetarian,
  DietaryWhole30,
} from 'src/shared/design-system/Icon';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { Colors } from 'web-common/src/shared/styles';
import { BaseFilterModal } from '../BaseFilterModal';
import { useTrackFilterChange } from '../hooks/useTrackFilterChange';
import { Button, ButtonContainer, ButtonWrapper, Container, IconWrapper, Label } from './styles';

export const DIETARY_BUTTON_ICON_SIZE_PROPS = {
  height: '12px',
  width: '12px',
  color: Colors.GRAY_DARK_30,
};

export const DIETARY_BUTTONS_INFO = [
  { dietaryTag: DietaryTags.VEGETARIAN, icon: <DietaryVegetarian {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.VEGAN, icon: <DietaryVegan {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.GLUTEN_FREE, icon: <AllergenGluten {...DIETARY_BUTTON_ICON_SIZE_PROPS} isCrossed /> },
  { dietaryTag: DietaryTags.PALEO, icon: <DietaryPaleo {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.ORGANIC, icon: <DietaryOrganic {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.HALAL, icon: <DietaryHalal {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.WHOLE30, icon: <DietaryWhole30 {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.LOW_FODMAP, icon: <DietaryLowFODMAP {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.DAIRY_FREE, icon: <DietaryDairyFree {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
  { dietaryTag: DietaryTags.KOSHER, icon: <DietaryKosher {...DIETARY_BUTTON_ICON_SIZE_PROPS} /> },
];

interface DietaryFilterModalProps {
  header: string;
  isShowing: boolean;
  largeHeader: boolean;
  onHide: () => void;
}

export const DietaryFilterModal: React.FC<DietaryFilterModalProps> = ({ header, isShowing, largeHeader, onHide }) => {
  const match = useRouteMatch();
  const filters = useDishFirstExploreUrl({ match });
  const { updateQueryParams, dietaryFilters } = filters;
  const { trackFilterChange } = useTrackFilterChange({ filters });

  const onClick = useCallback(
    (dietaryTag: DietaryTags) => {
      const updates = {
        [QueryParams.DIETARY_FILTER]: arrayToggle(dietaryFilters, dietaryTag),
      };
      updateQueryParams(updates);
      trackFilterChange(updates);
    },
    [updateQueryParams, dietaryFilters, trackFilterChange]
  );
  return (
    <BaseFilterModal filters={filters} header={header} isShowing={isShowing} largeHeader={largeHeader} onHide={onHide}>
      <Container>
        <ButtonContainer>
          {DIETARY_BUTTONS_INFO.map(({ dietaryTag, icon }) => (
            <Button selected={dietaryFilters.includes(dietaryTag)} onClick={() => onClick(dietaryTag)} key={dietaryTag}>
              <ButtonWrapper>
                <IconWrapper>{icon}</IconWrapper>
                <Label>{dietaryTag}</Label>
              </ButtonWrapper>
            </Button>
          ))}
        </ButtonContainer>
      </Container>
    </BaseFilterModal>
  );
};
